import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { fetchTipos, criarSolicitacoes } from "../../api/apiService";
import "../../styles/TiposList.css";
import Navbar from "../../components/Navbar";
import { ClipLoader } from "react-spinners";
import imageCompression from "browser-image-compression";
import {
  Grid,
  CardContent,
  Checkbox,
  Card,
  Button,
  Typography,
  Box,
  TextField,
  Modal,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";

import { InfoOutlined } from "@mui/icons-material";

function TiposList() {
  const { categoria } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [tipos, setTipos] = useState([]);
  const [filteredTipos, setFilteredTipos] = useState([]);
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem(`cart_${categoria}`);
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [showModal, setShowModal] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTipo, setSelectedTipo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [protocolos, setProtocolos] = useState(null);
  const [pressed, setPressed] = useState(null);
  const [touchMoved, setTouchMoved] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);

  // Extrai o parâmetro `user` da URL
  const searchParams = new URLSearchParams(location.search);
  const user = searchParams.get("user");
  const handleInfoOpen = () => setOpenInfoModal(true);
  const handleInfoClose = () => setOpenInfoModal(false);

  useEffect(() => {
    const getTipos = async () => {
      setLoading(true);
      try {
        // Passa o user como parâmetro ao fetchTipos
        const data = await fetchTipos(categoria, user);
        setTipos(data);
        setFilteredTipos(data);
      } catch (err) {
        setError("Falha ao carregar os dados.");
      } finally {
        setLoading(false);
      }
    };
    getTipos();
  }, [categoria, user]);

  useEffect(() => {
    localStorage.setItem(`cart_${categoria}`, JSON.stringify(cart));
  }, [cart, categoria]);

  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = tipos.filter((tipo) =>
      tipo.nome.toLowerCase().includes(term)
    );
    setFilteredTipos(filtered);
  };

  const handlePressStart = (tipoId) => {
    setPressed(tipoId);
    setTouchMoved(false);
  };

  const handleTouchMove = () => {
    setTouchMoved(true);
  };

  const handlePressEnd = (tipoId, event) => {
    event.stopPropagation(); // Previne que o evento "bata" em outros elementos

    if (!touchMoved && pressed === tipoId) {
      const tipoSelecionado = filteredTipos.find((t) => t.id === tipoId);

      if (
        tipoSelecionado &&
        !tipoSelecionado.jaSolicitado &&
        !isTipoInCart(tipoId)
      ) {
        openModal(tipoSelecionado);
      }
    }
    setPressed(null);
  };

  const isTipoInCart = (tipoId) => cart.some((item) => item.id === tipoId);

  const addToCart = (tipo) => {
    setCart((prevCart) => [...prevCart, tipo]);
  };

  const removeFromCart = (tipoId) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== tipoId));
  };

  const finalizeCart = async () => {
    if (!cart.length) {
      setError("O carrinho está vazio.");
      return;
    }

    const tipoServicoIds = cart.map((item) => item.id);

    try {
      setLoading(true);

      // Passa o user como argumento na chamada
      const response = await criarSolicitacoes(
        tipoServicoIds,
        selectedFile,
        user
      );
      setCart([]);
      localStorage.removeItem(`cart_${categoria}`);
      setSelectedFile(null);
      setSelectedTipo(null);
      setProtocolos(response); // Armazena os protocolos retornados
      setShowCartModal(false);
    } catch (err) {
      setError("Erro ao criar solicitação. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (tipo) => {
    setSelectedTipo({ ...tipo, requerImagem: tipo.requerImagem ?? false });
    setShowModal(true);
    setConfirmChecked(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTipo(null);
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Apenas arquivos de imagem são permitidos.");
        setSelectedFile(null);
        return;
      }

      try {
        // Opções de compressão
        const options = {
          maxSizeMB: 2, // Tamanho máximo do arquivo em MB
          maxWidthOrHeight: 1920, // Largura ou altura máxima
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);

        if (compressedFile.size > 10 * 1024 * 1024) {
          setError("Mesmo após compressão, o arquivo excede 10 MB.");
          setSelectedFile(null);
          return;
        }

        setError("");
        setSelectedFile(compressedFile);
      } catch (error) {
        console.error("Erro ao comprimir a imagem:", error);
        setError("Erro ao processar a imagem. Tente novamente.");
      }
    }
  };

  const handleConfirm = () => {
    if (!confirmChecked) return;

    addToCart(selectedTipo);
    closeModal();
  };

  const closeProtocolModal = () => {
    setProtocolos(null);
    navigate("/home");
  };

  return (
    <Box className="main-content">
      <Navbar />
      <Box className="tipos-list-container">
        <Typography variant="h4" gutterBottom>
          {categoria}
        </Typography>
        {error && (
          <Typography color="error" variant="body2" role="alert">
            {error}
          </Typography>
        )}

        {loading && !tipos.length ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            mt={2}
          >
            <ClipLoader color="#007bff" size={30} />
            <Typography variant="body2">Carregando...</Typography>
          </Box>
        ) : (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <TextField
                fullWidth
                variant="outlined"
                label="Buscar serviço..."
                value={searchTerm}
                aria-label="Campo de busca para serviços disponíveis"
                onChange={handleSearch}
              />
              {cart.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    setShowCartModal(true);
                  }}
                  sx={{ ml: 2 }}
                  aria-label={`Ver solicitações. ${cart.length} itens no carrinho`}
                >
                  <Typography variant="body2" aria-live="polite">
                      Ver Solicitações ({cart.length})
                  </Typography>
                </Button>
              )}
            </Box>

            <Grid container spacing={3}>
              {filteredTipos.map((tipo) => (
                <Grid item xs={12} sm={6} md={4} key={tipo.id}>
                <Card
                  elevation={3}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    cursor: "pointer",
                    transform: pressed === tipo.id ? "scale(0.95)" : "scale(1)",
                    transition: "transform 0.15s ease-in-out",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.07)" },
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Adicionar ${tipo.nome} ao pedido`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handlePressEnd(tipo.id, e);
                    }
                  }}
                  onTouchStart={() => handlePressStart(tipo.id)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={(event) => { 
                    event.preventDefault(); 
                    event.stopPropagation(); 
                    handlePressEnd(tipo.id, event);
                  }}
                  onMouseDown={() => handlePressStart(tipo.id)}
                  onMouseUp={(event) => handlePressEnd(tipo.id, event)}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {tipo.nome}
                    </Typography>
                    <Button
                      variant="contained"
                      color={
                        tipo.jaSolicitado || isTipoInCart(tipo.id)
                          ? "secondary"
                          : "primary"
                      }
                      disabled={tipo.jaSolicitado || isTipoInCart(tipo.id)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openModal(tipo);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openModal(tipo);
                      }}
                      title={
                        tipo.jaSolicitado
                          ? "Você só pode solicitar este serviço uma vez. Aguarde ser aceito ou negado para solicitar novamente."
                          : ""
                      }
                    >
                      {tipo.jaSolicitado
                        ? "Já Solicitado"
                        : isTipoInCart(tipo.id)
                        ? "Já Adicionado"
                        : "Adicionar ao Pedido"}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>              
              ))}
            </Grid>
          </>
        )}

        {showModal && selectedTipo && (
          <Modal open={showModal} onClose={closeModal}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "white",
                borderRadius: "16px",
                boxShadow: "0 10px 20px rgba(0, 0, 0, 0.3)",
                p: 4,
                maxWidth: "500px",
                width: "90%",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#007bff",
                  marginBottom: "16px",
                }}
              >
                Confirmar Solicitação
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#dc3545",
                  fontWeight: "1",
                  marginBottom: "16px",
                  lineHeight: "1.5",
                }}
              >
                <h6>
                  <strong className="modal-warning-title">Atenção:</strong>
                </h6>
                <br />{" "}
                <p>
                  Esta ação não envia automaticamente ao clicar no botão{" "}
                  <b>Adicionar Pedido</b>.<br /> Acesse <b>Ver Solicitações</b>{" "}
                  e clique em <b>Finalizar Solicitação</b>.<br /> Alguns itens
                  podem exigir o envio de comprovação.
                </p>
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={confirmChecked}
                    onChange={(e) => setConfirmChecked(e.target.checked)}
                    sx={{
                      "&.Mui-checked": {
                        color: "#007bff",
                      },
                      transform: "scale(1.2)",
                      marginRight: "8px",
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "16px" }}>
                    Confirmo que li e entendi as instruções.
                  </Typography>
                }
                sx={{ marginBottom: "16px" }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: confirmChecked ? "#28a745" : "#ccc",
                    color: "white",
                    fontWeight: "bold",
                    padding: "12px 24px",
                    textTransform: "uppercase",
                    "&:hover": {
                      backgroundColor: confirmChecked ? "#218838" : "#ccc",
                    },
                  }}
                  aria-label="Adicionar este pedido à lista de solicitações"
                  onClick={handleConfirm}
                  disabled={!confirmChecked}
                >
                  Adicionar Pedido
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    fontWeight: "bold",
                    padding: "12px 24px",
                    textTransform: "uppercase",
                    "&:hover": {
                      backgroundColor: "#c82333",
                    },
                  }}
                  aria-label="Cancelar a adição de pedido"
                  onClick={closeModal}
                >
                  Cancelar
                </Button>
              </Box>
            </Box>
          </Modal>
        )}

{showCartModal && (
  <Modal
    open={showCartModal}
    onClose={() => setShowCartModal(false)}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Box
      sx={{
        bgcolor: "background.paper",
        p: 3,
        borderRadius: 3,
        boxShadow: 24,
        maxWidth: 500,
        width: "90%",
        textAlign: "center",
        animation: "fadeIn 0.3s ease-in-out",
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ color: "#007bff" }}>
        Solicitações
      </Typography>

      {cart.length > 0 ? (
        <>
          {cart.map((item) => (
            <Box
              key={item.id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={2}
              mb={2}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: "12px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                transition: "all 0.2s ease-in-out",
                "&:hover": { backgroundColor: "#e0e0e0" },
              }}
            >
              <Typography sx={{ fontWeight: "bold", color: "#333" }}>
                {item.nome}
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => removeFromCart(item.id)}
                aria-label={`Remover ${item.nome} do carrinho`}
              >
                Remover
              </Button>
            </Box>
          ))}

          {cart.some((item) => item.requerImagem) && (
            <Box mt={2}>
              <Typography color="error" variant="body2" gutterBottom>
                Os seguintes itens requerem envio de comprovação: {cart
                  .filter((item) => item.requerImagem)
                  .map((item) => item.nome)
                  .join(", ")}
                .
              </Typography>
              <Button
                variant="contained"
                component="label"
                sx={{
                  backgroundColor: "#007bff",
                  "&:hover": { backgroundColor: "#0056b3" },
                  marginTop: "10px",
                  transition: "background 0.3s ease-in-out",
                }}
                fullWidth
              >
                Enviar Comprovação (máx: 10MB)
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept="image/*"
                  aria-label="Selecionar arquivo de comprovação, aceito apenas imagens"
                />
              </Button>
              {selectedFile && (
                <Typography variant="body2" mt={1} aria-live="polite">
                  Arquivo selecionado: {selectedFile.name}
                </Typography>
              )}
            </Box>
          )}

          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="outlined"
              startIcon={<InfoOutlined />}
              onClick={handleInfoOpen}
            >
              O que é a comprovação?
            </Button>
          </Box>

          <Modal open={openInfoModal} onClose={handleInfoClose}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                bgcolor: "white",
                p: 4,
                borderRadius: 3,
                boxShadow: 24,
                maxWidth: 500,
                width: "90%",
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                O que é a Comprovação?
              </Typography>
              <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                A comprovação é o documento necessário para validar sua solicitação. Pode ser:
                <ul style={{ paddingLeft: '20px', marginTop: '10px', marginBottom: '10px' }}>
                  <li>
                    <strong>Encaminhamento Médico:</strong> Documento emitido pelo médico que justifica a necessidade do atendimento solicitado.
                  </li>
                  <li>
                    <strong>Cartão de Transporte do Hospital:</strong> Caso a solicitação seja referente a transporte hospitalar, é necessário apresentar o cartão de transporte fornecido pelo hospital.
                  </li>
                </ul>
                Envie o documento correto para que possamos validar sua solicitação adequadamente.
              </Typography>

              <Button
                variant="contained"
                onClick={handleInfoClose}
                sx={{ mt: 2 }}
              >
                Fechar
              </Button>
            </Box>
          </Modal>
          
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ marginTop: "20px" }}
          >
            <strong>Atenção:</strong> Esta ação não pode ser desfeita. Verifique com cuidado antes de finalizar.
          </Typography>

          <Box mt={3} display="flex" gap={2}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              sx={{
                textTransform: "uppercase",
                fontWeight: "bold",
                transition: "background 0.3s ease-in-out",
                "&:hover": { backgroundColor: "#218838" },
              }}
              aria-label="Botão para finalizar solicitação"
              onClick={finalizeCart}
              disabled={
                cart.length === 0 ||
                (cart.some((item) => item.requerImagem) && !selectedFile) ||
                loading
              }
            >
              {loading ? (
                <CircularProgress size={24} style={{ color: "white" }} />
              ) : (
                "Finalizar Solicitação"
              )}
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              sx={{
                textTransform: "uppercase",
                fontWeight: "bold",
                transition: "background 0.3s ease-in-out, color 0.3s ease-in-out",
                "&:hover": { backgroundColor: "#c82333", color: "white" },
              }}
              onClick={() => setShowCartModal(false)}
              aria-label="Fechar o modal de solicitações"
            >
              Fechar
            </Button>
          </Box>
        </>
      ) : (
        <Typography color="textSecondary" mt={2}>
          Nenhuma solicitação no carrinho.
        </Typography>
      )}
    </Box>
  </Modal>
)}


{protocolos && (
  <Modal open={Boolean(protocolos)} onClose={closeProtocolModal}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        bgcolor: "background.paper",
        p: 4,
        borderRadius: 3,
        boxShadow: 24,
        textAlign: "center",
        animation: "fadeIn 0.3s ease-in-out",
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ color: "#007bff" }}>
        Solicitações Criadas
      </Typography>
      {protocolos.map((protocolo) => (
        <Box
          key={protocolo.id}
          mb={2}
          sx={{
            backgroundColor: "#f9f9f9",
            borderRadius: "12px",
            p: 2,
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            transition: "all 0.2s ease-in-out",
            "&:hover": { backgroundColor: "#e0e0e0" },
          }}
        >
          <Typography variant="body1">Serviço: {protocolo.nomeServico}</Typography>
          <Typography variant="body2">
            <strong>Protocolo: {protocolo.protocolo}</strong>
          </Typography>
        </Box>
      ))}
      <Button
        variant="outlined"
        onClick={closeProtocolModal}
        fullWidth
        sx={{ mt: 2, textTransform: "uppercase", fontWeight: "bold" }}
      >
        Fechar
      </Button>
    </Box>
  </Modal>
)}

      </Box>
    </Box>
  );
}

export default TiposList;
